import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Row, Col, Spinner, Alert, Badge } from 'react-bootstrap';
import { PlusLg, Trash3, CheckCircle } from 'react-bootstrap-icons';

import { useLanguage } from '../../../../../API/Context/LanguageContext';
import { useUser } from '../../../../../API/Context/UserContext';
import { useUserWorkAssignment } from '../../../../../API/Hooks/useUserWorkAssignment';

import { ContactPointsSection, type ContactPoint } from './workAssignment/ContactPointsSection';
import { OfficeHoursSection } from './workAssignment/OfficeHoursSection';

import VocabProMenu from '../../../../../ConfigFiles/multilingual/VocabProMenu';
import { VocabButtons as btn_vocab } from '../../../../../ConfigFiles/multilingual/VocabButtons';
import { timeToMinutes, validateSchedule, type WorkingDaySchedule } from '../../../../../Utils/timeUtils';
import type { WorkLocationAndAssignmentUpdate } from '../../../../../Types/schema-helpers';

interface WorkAssignmentWidgetProps {
  onSaveSuccess?: () => void;
}

interface LocalWorkAssignment {
  localId: string;
  location_id: string;
  location_name: string;
  street: string;
  number: string;
  phone: string;
  interior_number: number | null;
  contact_points: ContactPoint[];
  manage_agenda: boolean;
  slot_size: number;
  office_hours: Record<string, { start: number; end: number }[]>;
  timezone: string;
  deleted?: boolean;
}

export const WorkAssignmentWidget: React.FC<WorkAssignmentWidgetProps> = ({ onSaveSuccess }) => {
  const { t } = useLanguage();
  const { user, updateProfileField } = useUser();
  const { updateWorkAssignment, loading: isSavingHook } = useUserWorkAssignment();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<LocalWorkAssignment[]>([]);

  const handleCopyMondayToWeekdays = (localId: string) => {
  setAssignments((prev) =>
    prev.map((wa) => {
      if (wa.localId !== localId) return wa;

      // Get Monday's intervals ('1')
      const mondayIntervals = wa.office_hours['1'] || [];

      // Deep clone Monday intervals to Tuesday ('2'), Wednesday ('3'), Thursday ('4'), and Friday ('5')
      const updatedOfficeHours = {
        ...wa.office_hours,
        '2': mondayIntervals.map((i) => ({ ...i })),
        '3': mondayIntervals.map((i) => ({ ...i })),
        '4': mondayIntervals.map((i) => ({ ...i })),
        '5': mondayIntervals.map((i) => ({ ...i })),
      };

      return {
        ...wa,
        office_hours: updatedOfficeHours,
      };
    })
  );
};

  useEffect(() => {
    if (user?.professional?.work_assignment) {
      const mapped = user.professional.work_assignment.map((wa: any, idx: number) => {
        const locDetails = wa.location_details || {};
        return {
          localId: wa.location_id || `wa-${idx}`,
          location_id: wa.location_id || '',
          location_name: locDetails.location_name || '',
          street: locDetails.street || '',
          number: locDetails.number || '',
          phone: locDetails.phone || '',
          interior_number: wa.interior_number ?? null,
          contact_points: wa.contact_points || [],
          manage_agenda: wa.manage_agenda ?? true,
          slot_size: wa.slot_size ?? 30,
          office_hours: wa.office_hours || {
            '0': [], '1': [], '2': [], '3': [], '4': [], '5': [], '6': []
          },
          timezone: wa.timezone || 'America/Mexico_City',
          deleted: false,
        };
      });
      setAssignments(mapped);
    }
  }, [user]);

  const handleFieldChange = (localId: string, field: keyof LocalWorkAssignment, value: any) => {
    setAssignments((prev) =>
      prev.map((wa) => (wa.localId === localId ? { ...wa, [field]: value } : wa))
    );
  };

  const handleRemoveAssignment = (localId: string) => {
    setAssignments((prev) =>
      prev.map((wa) => (wa.localId === localId ? { ...wa, deleted: true } : wa))
    );
  };

  // Contact Handlers
  const handleAddContact = (localId: string) => {
    setAssignments((prev) =>
      prev.map((wa) => {
        if (wa.localId !== localId) return wa;
        return {
          ...wa,
          contact_points: [
            ...wa.contact_points,
            { number: '', extension: null, is_whatsapp: false, is_telegram: false, label: 'Office' },
          ],
        };
      })
    );
  };

  const handleContactChange = (localId: string, contactIdx: number, field: keyof ContactPoint, value: any) => {
    setAssignments((prev) =>
      prev.map((wa) => {
        if (wa.localId !== localId) return wa;
        const updatedContacts = [...wa.contact_points];
        if (updatedContacts[contactIdx]) {
          updatedContacts[contactIdx] = { ...updatedContacts[contactIdx]!, [field]: value };
        }
        return { ...wa, contact_points: updatedContacts };
      })
    );
  };

  const handleRemoveContact = (localId: string, contactIdx: number) => {
    setAssignments((prev) =>
      prev.map((wa) => {
        if (wa.localId !== localId) return wa;
        return {
          ...wa,
          contact_points: wa.contact_points.filter((_, idx) => idx !== contactIdx),
        };
      })
    );
  };

  // Interval Handlers
  const handleIntervalChange = (
    localId: string,
    dayStr: string,
    intervalIdx: number,
    bound: 'start' | 'end',
    timeStr: string
  ) => {
    const minutes = timeToMinutes(timeStr);
    setAssignments((prev) =>
      prev.map((wa) => {
        if (wa.localId !== localId) return wa;
        const dayIntervals = [...(wa.office_hours[dayStr] || [])];
        if (dayIntervals[intervalIdx]) {
          dayIntervals[intervalIdx] = {
            ...dayIntervals[intervalIdx]!,
            [bound]: minutes >= 0 ? minutes : 0,
          };
        }
        return {
          ...wa,
          office_hours: { ...wa.office_hours, [dayStr]: dayIntervals },
        };
      })
    );
  };

  const handleAddInterval = (localId: string, dayStr: string) => {
    setAssignments((prev) =>
      prev.map((wa) => {
        if (wa.localId !== localId) return wa;
        const dayIntervals = [...(wa.office_hours[dayStr] || []), { start: 540, end: 1020 }];
        return {
          ...wa,
          office_hours: { ...wa.office_hours, [dayStr]: dayIntervals },
        };
      })
    );
  };

  const handleRemoveInterval = (localId: string, dayStr: string, intervalIdx: number) => {
    setAssignments((prev) =>
      prev.map((wa) => {
        if (wa.localId !== localId) return wa;
        const dayIntervals = (wa.office_hours[dayStr] || []).filter((_, idx) => idx !== intervalIdx);
        return {
          ...wa,
          office_hours: { ...wa.office_hours, [dayStr]: dayIntervals },
        };
      })
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    const activeTarget = assignments.find((a) => !a.deleted);

    if (!activeTarget) {
      setError(t(VocabProMenu, 'no_assignments'));
      setSaving(false);
      return;
    }

    try {
      // 1. Schedule Validation
      const scheduleToValidate: WorkingDaySchedule[] = Array.from({ length: 7 }, (_, idx) => {
        const dayKey = String(idx);
        const intervals = activeTarget.office_hours[dayKey] || [];
        return {
          dayIdx: idx,
          working: intervals.length > 0,
          intervals: intervals.map((i) => ({ start: i.start, end: i.end })),
        };
      });

      const validation = validateSchedule(scheduleToValidate);
      if (!validation.isOverallValid) {
        setError(t(VocabProMenu, 'schedule_validation_error'));
        setSaving(false);
        return;
      }

      // 2. Build Structured Unified Payload matching backend contract
      const payload: WorkLocationAndAssignmentUpdate = {
        location_id: activeTarget.location_id || null,
        location_data: {
          location_name: activeTarget.location_name,
          street: activeTarget.street,
          number: activeTarget.number,
          phone: activeTarget.phone,
        },
        work_assignment_data: {
          interior_number: activeTarget.interior_number,
          contact_points: activeTarget.contact_points,
          manage_agenda: activeTarget.manage_agenda,
          slot_size: Math.max(1, activeTarget.slot_size), // Safeguard slot_size >= 1
          office_hours: activeTarget.office_hours,
          timezone: activeTarget.timezone,
        },
      };

      // 3. Delegate execution to custom hook
      const updatedAssignment = await updateWorkAssignment(payload);

      // 4. Update local user context state
      if (user?.professional) {
        const updatedAssignments = (user.professional.work_assignment || []).map((existing: any) =>
          existing.location_id === updatedAssignment.location_id
            ? { ...existing, ...updatedAssignment }
            : existing
        );

        updateProfileField('professional', {
          ...user.professional,
          work_assignment: updatedAssignments,
        });
      }

      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      console.error(err);
      setError(t(VocabProMenu, 'work_assignment_save_error'));
    } finally {
      setSaving(false);
    }
  };

  const activeAssignments = assignments.filter((wa) => !wa.deleted);
  const isSaving = saving || isSavingHook;

  return (
    <div className="d-flex flex-column gap-4 animate-in">
      {/* Subheader Bar */}
      <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
        <div>
          <p className="m-0 text-muted small">{t(VocabProMenu, 'practice_details_desc')}</p>
        </div>

        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={isSaving}
          className="d-flex align-items-center gap-2 shadow-sm"
        >
          {isSaving ? <Spinner size="sm" animation="border" /> : <CheckCircle size={16} />}
          <span>{t(btn_vocab, 'save')}</span>
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <h5 className="m-0 fw-bold">{t(VocabProMenu, 'work_assignment_title')}</h5>
            <Badge bg="secondary">{activeAssignments.length}</Badge>
          </div>
        </Card.Header>

        <Card.Body>
          {activeAssignments.length === 0 ? (
            <p className="text-center text-muted py-4 m-0">
              {t(VocabProMenu, 'no_assignments')}
            </p>
          ) : (
            <div className="d-flex flex-column gap-4">
              {activeAssignments.map((wa) => (
                <div key={wa.localId} className="p-3 border rounded bg-light position-relative">
                  <Button
                    variant="link"
                    className="text-danger position-absolute top-0 end-0 m-2 p-1"
                    onClick={() => handleRemoveAssignment(wa.localId)}
                  >
                    <Trash3 size={16} />
                  </Button>

                  <Row className="g-3 mb-3">
                    <Col md={6}>
                      <Form.Label className="small fw-semibold">{t(VocabProMenu, 'interior_number')}</Form.Label>
                      <Form.Control
                        type="number"
                        value={wa.interior_number ?? ''}
                        onChange={(e) =>
                          handleFieldChange(
                            wa.localId,
                            'interior_number',
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Label className="small fw-semibold">{t(VocabProMenu, 'slot_size')}</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        value={wa.slot_size}
                        onChange={(e) => handleFieldChange(wa.localId, 'slot_size', Math.max(1, Number(e.target.value)))}
                      />
                    </Col>
                    <Col md={12}>
                      <Form.Check
                        type="switch"
                        id={`manage-agenda-${wa.localId}`}
                        label={t(VocabProMenu, 'manage_agenda')}
                        checked={wa.manage_agenda}
                        onChange={(e) => handleFieldChange(wa.localId, 'manage_agenda', e.target.checked)}
                      />
                    </Col>
                  </Row>

                  <ContactPointsSection
                    localId={wa.localId}
                    contactPoints={wa.contact_points}
                    onAddContact={() => handleAddContact(wa.localId)}
                    onChangeContact={(idx, field, val) => handleContactChange(wa.localId, idx, field, val)}
                    onRemoveContact={(idx) => handleRemoveContact(wa.localId, idx)}
                  />

                  <OfficeHoursSection
                  officeHours={wa.office_hours}
                  onIntervalChange={(dayStr, idx, bound, timeStr) =>
                    handleIntervalChange(wa.localId, dayStr, idx, bound, timeStr)
                  }
                  onAddInterval={(dayStr) => handleAddInterval(wa.localId, dayStr)}
                  onRemoveInterval={(dayStr, idx) => handleRemoveInterval(wa.localId, dayStr, idx)}
                  onCopyMondayToWeekdays={() => handleCopyMondayToWeekdays(wa.localId)}
                />
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default WorkAssignmentWidget;